import { motion } from "motion/react";
import { ArrowRight, Check } from "lucide-react";
import { useI18n } from "../i18n";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
  },
};

/** Abertura do site — editorial: serifada gigante alinhada à esquerda,
    palavra em destaque com marca-texto âmbar, papel pontilhado ao fundo. */
export function Hero() {
  const { t } = useI18n();

  return (
    <section id="topo" className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24">
      {/* fundo: papel técnico pontilhado */}
      <div aria-hidden className="absolute inset-0">
        <div className="bg-dots bg-dots-fade absolute inset-0" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative mx-auto w-full max-w-6xl px-6"
      >
        <motion.div variants={item}>
          <Badge variant="live">
            <span className="animate-pulse-dot size-1.5 rounded-full bg-accent" />
            {t("hero.chip")}
          </Badge>
        </motion.div>

        <motion.h1
          variants={item}
          className="mt-8 max-w-4xl font-display text-[clamp(2.6rem,7vw,4.8rem)] leading-[1.04] font-semibold tracking-[-0.03em] text-balance"
        >
          {t("hero.titulo.a")}
          <span className="text-mark">{t("hero.titulo.destaque")}</span>
          {t("hero.titulo.b")}
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div variants={item} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button href="#contato" size="lg" className="group w-full sm:w-auto">
            {t("hero.cta1")}
            <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
          <Button href="#produtos" size="lg" variant="secondary" className="w-full sm:w-auto">
            {t("hero.cta2")}
          </Button>
        </motion.div>

        {/* régua de garantias — hairline em cima, três checks lado a lado */}
        <motion.ul
          variants={item}
          className="mt-14 grid gap-4 border-t border-line pt-6 sm:mt-16 sm:grid-cols-3 sm:gap-6"
        >
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <span className="grid size-[18px] shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                <Check className="size-3" strokeWidth={3} />
              </span>
              {t(`hero.check${n}`)}
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}
